import { Card, Text, TextLink } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren } from 'react';
import { DEFAULT_CURRENCY } from '../../../constants/currency';
import { useHandlers } from '../../../context/page-context';
import { useSettings } from '../../../context/settings-context';
import type { CompanyNetTerms, FullCompanyContext } from '../types/company';
import { DetailRow } from './detail-row';

type ContextCardProps = PropsWithChildren<
  FullCompanyContext & {
    name: string;
    company?: boolean;
    subtitle?: string;
    /** When provided, renders an edit action in the card header. */
    onEdit?: () => void;
  }
>;
export function ContextCard({
  customer_group,
  accounting_id,
  price_lists,
  sales_agent_groups,
  payment_on_account,
  name,
  subtitle,
  company,
  onEdit,
  children,
}: ContextCardProps) {
  const { navigationHandler } = useHandlers();
  const { baseCurrency } = useSettings();

  return (
    <Card>
      <Card.Header
        badges={
          customer_group
            ? [
                {
                  variant: 'green',
                  text: customer_group,
                },
              ]
            : []
        }
        headingLevel="h2"
        icon={company ? 'premium' : undefined}
        collapsible={
          !!children ||
          !!payment_on_account ||
          !!price_lists ||
          !!sales_agent_groups ||
          !!accounting_id
        }
        primaryAction={onEdit ? { icon: 'edit', variant: 'bare', onClick: onEdit } : undefined}
      >
        <Text bold>{name}</Text>
        {subtitle && (
          <Text variant="secondary" size="sm">
            {subtitle}
          </Text>
        )}
      </Card.Header>
      <Card.Body>
        <DetailRow
          label={t`features:sections.components.context_card.accounting_id`}
          value={accounting_id}
        />
        {price_lists !== null &&
          (price_lists.length === 0 ? (
            <Text variant="secondary">
              {company
                ? t`features:sections.components.context_card.price_lists.empty_company`
                : t`features:sections.components.context_card.price_lists.empty_section`}
            </Text>
          ) : (
            <div>
              <Text>
                {company
                  ? t`features:sections.components.context_card.price_lists.title_company`
                  : t`features:sections.components.context_card.price_lists.title_section`}
              </Text>
              <div className="flex flex-wrap gap-2">
                {price_lists.map((slug) => (
                  <TextLink
                    key={slug}
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      navigationHandler.navigate(`pricing/lists/edit/${encodeURIComponent(slug)}`)
                    }
                  >
                    {slug}
                  </TextLink>
                ))}
              </div>
            </div>
          ))}
        {sales_agent_groups !== null &&
          (sales_agent_groups.length === 0 ? (
            <Text variant="secondary">
              {company
                ? t`features:sections.components.context_card.sales_agent_groups.empty_company`
                : t`features:sections.components.context_card.sales_agent_groups.empty_section`}
            </Text>
          ) : (
            <div>
              <Text>
                {company
                  ? t`features:sections.components.context_card.sales_agent_groups.title_company`
                  : t`features:sections.components.context_card.sales_agent_groups.title_section`}
              </Text>
              <div className="flex flex-wrap gap-2">
                {sales_agent_groups.map((sag) => (
                  <Text variant="secondary" key={sag}>
                    <em>{sag}</em>
                  </Text>
                ))}
              </div>
            </div>
          ))}
        {payment_on_account && (
          <Card>
            <Card.Header collapsible headingLevel="h4">
              {t`features:sections.components.context_card.payment_on_account.title`}
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-2">
                <DetailRow
                  label={t`features:sections.components.context_card.payment_on_account.balance`}
                  value={
                    payment_on_account.balance !== null
                      ? `${payment_on_account.balance} ${payment_on_account.currency_code || baseCurrency || DEFAULT_CURRENCY}`
                      : null
                  }
                />
                <DetailRow
                  label={t(
                    'features:sections.components.context_card.payment_on_account.credit_limit',
                  )}
                  value={
                    payment_on_account.credit_limit !== null
                      ? `${payment_on_account.credit_limit} ${payment_on_account.currency_code || baseCurrency || DEFAULT_CURRENCY}`
                      : null
                  }
                />
                <DetailRow
                  label={t(
                    'features:sections.components.context_card.payment_on_account.net_terms.title',
                  )}
                  value={
                    payment_on_account.net_terms &&
                    (
                      {
                        '7_days': t`features:sections.components.context_card.payment_on_account.net_terms.7_days`,
                        '15_days': t`features:sections.components.context_card.payment_on_account.net_terms.15_days`,
                        '30_days': t`features:sections.components.context_card.payment_on_account.net_terms.30_days`,
                        '45_days': t`features:sections.components.context_card.payment_on_account.net_terms.45_days`,
                        '60_days': t`features:sections.components.context_card.payment_on_account.net_terms.60_days`,
                        '90_days': t`features:sections.components.context_card.payment_on_account.net_terms.90_days`,
                      } satisfies Record<CompanyNetTerms, string>
                    )[payment_on_account.net_terms]
                  }
                />
              </div>
            </Card.Body>
          </Card>
        )}
        {children}
      </Card.Body>
    </Card>
  );
}
